import { useNavigate } from "@solidjs/router";
import { ProjectSwitcherModal as CoreProjectSwitcherModal } from "@storytell/core";
import type { ProjectId } from "@storytell/types";
import type { Component } from "solid-js";
import { authGateStatus } from "~/config/authGating";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

interface ProjectSwitcherModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional: Start in "form" view to show create project form directly */
  initialView?: "grid" | "form";
  /**
   * When set, a created project is reported here instead of switching to it —
   * used when the switcher is nested inside another flow that needs the new
   * project id.
   */
  onProjectCreated?: (projectId: ProjectId) => void;
  /**
   * When set, project creation is constrained to this organization — used by a
   * flow whose destination must stay in one org (a live-session move). The org
   * selector then offers only this org, and none when the user doesn't own it.
   */
  restrictToOrganizationId?: () => string | undefined;
}

export const ProjectSwitcherModal: Component<ProjectSwitcherModalProps> = (props) => {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();
  const scope = useOrgScope();

  // Switching projects leaves any in-flight chat generating server-side; it
  // reconnects via playback when the user returns. Don't interrupt it here.
  // A project in another organization switches the organization too, because
  // switchToProject persists org, workspace and project together.
  const handleProjectClick = (projectId: ProjectId) => {
    if (wire.activeContext.activeContext()?.projectId === projectId) {
      navigate(`/project/${projectId}`);
    } else {
      trackFeature({ feature: "nav.project_switch", via: "menu" });
      wire.userContext.switchToProject(projectId);
    }
    props.onOpenChange(false);
  };

  // Gate the in-modal "Create project" button: if guest, close the switcher and open the auth gate instead.
  const handleBeforeCreateProject = (): boolean => {
    const status = authGateStatus(wire.auth, "createProject");
    if (status === "allow") return true;
    if (status === "pending") return false;
    props.onOpenChange(false);
    wire.ui.authGate.openModal({
      title: t("modal_signup_create_projects_title"),
      description: t("modal_signup_create_projects_desc"),
      pendingAction: { type: "project", data: undefined },
      feature: "createProject",
    });
    return false;
  };

  // Called only once a project has been created.
  const handleNavigateToProject = (projectId: ProjectId): Promise<boolean> => {
    trackFeature({ feature: "project", action: "create", via: "button" });
    if (props.onProjectCreated) {
      props.onProjectCreated(projectId);
      return Promise.resolve(true);
    }
    wire.userContext.switchToProject(projectId);
    return Promise.resolve(true);
  };

  // The list crosses organization boundaries, so a row outside the focused
  // organization names its own, and its owner — otherwise it reads as belonging
  // to the one the sidebar names, and organizations still on the signup default
  // all share a name. Inside it, repeating the name on every row is noise.
  const organizationName = (organizationId: string) => {
    if (!scope.isMultiOrg() || organizationId === scope.currentOrganization()?.organizationId) return undefined;
    const tenant = scope.organizations().find((candidate) => candidate.organizationId === organizationId);
    return [scope.organizationNameById(organizationId), tenant?.organizationOwnerEmail].filter(Boolean).join(" · ");
  };

  const isOwned = (organizationId: string) =>
    scope.ownedOrganizations().some((tenant) => tenant.organizationId === organizationId);

  // Creating a project needs ownership at organization scope, and the focused
  // organization can be one the user only reaches through a grant on a project.
  const defaultOrganizationId = () => {
    const restricted = props.restrictToOrganizationId?.();
    if (restricted) return restricted;
    const focused = scope.currentOrganization()?.organizationId;
    if (focused && isOwned(focused)) return focused;
    return wire.primaryOrganizationId() ?? scope.ownedOrganizations()[0]?.organizationId;
  };

  const createOrganizations = () =>
    scope
      .ownedOrganizations()
      .filter((tenant) => {
        const restricted = props.restrictToOrganizationId?.();
        return !restricted || tenant.organizationId === restricted;
      })
      .map((tenant) => ({ id: tenant.organizationId, name: scope.organizationName(tenant) }));

  const unavailableOrganizationName = () => {
    const restricted = props.restrictToOrganizationId?.();
    if (restricted) {
      if (isOwned(restricted)) return undefined;
      const tenant = scope.organizations().find((candidate) => candidate.organizationId === restricted);
      return tenant ? scope.organizationName(tenant) : undefined;
    }
    const focused = scope.currentOrganization();
    if (!focused || isOwned(focused.organizationId)) return undefined;
    return scope.organizationName(focused);
  };

  return (
    <CoreProjectSwitcherModal
      open={props.open}
      onOpenChange={props.onOpenChange}
      initialView={props.initialView}
      onBackFromCreate={props.initialView === "form" ? () => props.onOpenChange(false) : undefined}
      projects={wire.projects}
      userContext={wire.userContext}
      homeOrganizationId={defaultOrganizationId}
      createOrganizations={createOrganizations}
      unavailableOrganizationName={unavailableOrganizationName}
      authToken={wire.auth.token}
      onProjectClick={handleProjectClick}
      onNavigateToProject={handleNavigateToProject}
      onBeforeCreateProject={handleBeforeCreateProject}
      organizationName={organizationName}
      strings={{
        modalTitle: t("nav_projects"),
        addProject: t("project_switcher.create_project"),
        createNamed: t("project_switcher.create_named"),
        searchPlaceholder: t("project_switch_search_placeholder"),
        recentHeading: t("project_switcher.recent"),
        noResults: t("project_switcher.no_results"),
        currentProject: t("project_switcher.current"),
        roleCollaborator: t("organization.members.role_collaborator"),
        roleReader: t("organization.members.role_reader"),
        createTitle: t("projects_modal_create_title"),
        organizationLabel: t("project_create_organization_label"),
        organizationUnavailable: t("project_create_organization_unavailable"),
        createError: t("project_create_error"),
        billingHeading: t("project_create_billing_heading"),
        billingLearnMore: t("action.learn_more"),
        billingCallerLabel: t("project_create_billing_caller_label"),
        billingCallerDesc: t("project_create_billing_caller_desc"),
        billingOrgLabel: t("project_create_billing_org_label"),
        billingOrgDesc: t("project_create_billing_org_desc"),
      }}
    />
  );
};
